import {
  EventStreamContentType,
  fetchEventSource,
} from '@microsoft/fetch-event-source';
import {
  NotificationStreamEvent,
  type NotificationPushFrame,
  type NotificationReadyFrame,
  type NotificationStreamEndFrame,
} from '@gokrew/models/notifications';

export interface NotificationStreamHandlers {
  onReady: (frame: NotificationReadyFrame) => void;
  onNotification: (frame: NotificationPushFrame) => void;
  /** Server closed the stream, or it broke. The caller decides to reconnect. */
  onEnd: (frame: NotificationStreamEndFrame) => void;
}

const parseFrame = (raw: string) => {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return undefined;
  }
};

/**
 * Opens the notification SSE stream. Auth is a bearer header on this endpoint,
 * which the browser's own `EventSource` cannot send — hence `fetchEventSource`.
 *
 * Resolves once the stream closes, and rejects if it never opened — a non-2xx
 * open rejects with an error carrying `status`, since which failures are worth
 * retrying is the caller's policy, not this module's. Reconnects are the
 * caller's business too: `onerror` rethrows so the library's own retry loop
 * never competes with the caller's backoff.
 *
 * `url` is built by the app: the company and employee routes differ, and only
 * the app knows its own base path and token source.
 */
export const openNotificationStream = (
  url: string,
  getToken: () => Promise<string | null>,
  signal: AbortSignal,
  handlers: NotificationStreamHandlers
) =>
  fetchEventSource(url, {
    signal,
    // The token is read per attempt, not captured once: a stream outlives the
    // access token that opened it, and the library re-fetches on its own when
    // the tab is hidden and shown again.
    fetch: async (input, init) => {
      const token = await getToken();
      if (!token) throw new Error('No access token for notification stream');

      return fetch(input, {
        ...init,
        headers: { ...init?.headers, Authorization: `Bearer ${token}` },
      });
    },
    onopen: async (res) => {
      const contentType = res.headers.get('content-type') ?? '';
      if (res.ok && contentType.includes(EventStreamContentType)) return;

      throw Object.assign(
        new Error(`Notification stream failed with status ${res.status}`),
        { status: res.status }
      );
    },
    onmessage: (message) => {
      const payload = parseFrame(message.data);
      if (!payload) return;

      switch (message.event) {
        case NotificationStreamEvent.Ready:
          handlers.onReady(payload as NotificationReadyFrame);
          break;
        case NotificationStreamEvent.Notification:
        case NotificationStreamEvent.NotificationUpdated:
          handlers.onNotification(payload as NotificationPushFrame);
          break;
        case NotificationStreamEvent.Shutdown:
        case NotificationStreamEvent.SlowConsumer:
          handlers.onEnd(payload as NotificationStreamEndFrame);
          break;
      }
    },
    onerror: (error) => {
      throw error;
    },
  });
